import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { Button } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORY_META: Record<string, { icon: string; label: string; color: string }> = {
  scanner: { icon: 'barcode-outline', label: 'Scanner', color: '#6366F1' },
  printer: { icon: 'print-outline', label: 'Printer', color: '#0EA5E9' },
  camera: { icon: 'camera-outline', label: 'Camera', color: '#10B981' },
  other: { icon: 'hardware-chip-outline', label: 'Other', color: '#F59E0B' },
};

const STATUS_META: Record<string, { color: string; label: string }> = {
  online: { color: '#10B981', label: 'Online' },
  offline: { color: '#6B7280', label: 'Offline' },
  idle: { color: '#F59E0B', label: 'Idle' },
  error: { color: '#EF4444', label: 'Error' },
};

const SUB_TYPE_LABEL: Record<string, string> = {
  pos: 'POS Scanner',
  a4: 'A4',
  receipt: 'Receipt Printer',
};

function deviceSubLabel(device: any): string {
  if (device.category === 'scanner' && device.scannerType) return SUB_TYPE_LABEL[device.scannerType] || device.scannerType;
  if (device.category === 'printer' && device.printerType) return SUB_TYPE_LABEL[device.printerType] || device.printerType;
  return '';
}

// ─── Device Card ──────────────────────────────────────────────────────────────

const DeviceCard: React.FC<{
  device: any;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ device, onEdit, onDelete }) => {
  const styles = useThemedStyles(getStyles);
  const meta = CATEGORY_META[device.category] || CATEGORY_META.other;
  const statusMeta = STATUS_META[device.deviceStatus] || STATUS_META.offline;
  const sub = deviceSubLabel(device);

  return (
    <View style={styles.card}>
      <View style={[styles.cardIcon, { backgroundColor: meta.color + '18' }]}>
        <Ionicons name={meta.icon as any} size={26} color={meta.color} />
      </View>
      <View style={styles.cardBody}>
        <View style={styles.cardTitleRow}>
          <Text style={styles.cardName} numberOfLines={1}>{device.name}</Text>
          <View style={[styles.statusDot, { backgroundColor: statusMeta.color }]} />
          <Text style={[styles.statusLabel, { color: statusMeta.color }]}>{statusMeta.label}</Text>
        </View>
        <Text style={styles.cardCategory}>
          {meta.label}{sub ? ` · ${sub}` : ''}{device.location ? ` · ${device.location}` : ''}
        </Text>
        <Text style={styles.cardDetail}>
          {device.connectionType}{device.ipAddress ? ` · ${device.ipAddress}${device.port ? `:${device.port}` : ''}` : ''}
        </Text>
      </View>
      <View style={styles.cardActions}>
        <TouchableOpacity onPress={onEdit} style={styles.actionBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="pencil-outline" size={18} color={colors.textLight} />
        </TouchableOpacity>
        <TouchableOpacity onPress={onDelete} style={styles.actionBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Ionicons name="trash-outline" size={18} color={colors.error} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ─── Filter chips ─────────────────────────────────────────────────────────────

const FILTERS: { key: string | undefined; label: string }[] = [
  { key: undefined, label: 'All' },
  { key: 'scanner', label: 'Scanners' },
  { key: 'printer', label: 'Printers' },
  { key: 'camera', label: 'Cameras' },
  { key: 'other', label: 'Other' },
];

// ─── Screen ───────────────────────────────────────────────────────────────────

export const DevicesListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const { currentBusiness } = useAppStore();

  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async (reset = false) => {
    try {
      setError(null);
      if (reset) setLoading(true);
      const res = await apiService.getDevices({ category: categoryFilter as any, limit: 100 });
      if (res.statusCode === 1000 && res.data) {
        setDevices(res.data.devices || []);
        setTotal(res.data.pagination?.total || res.data.devices?.length || 0);
      } else {
        setError(res.errors?.[0]?.message || 'Failed to load devices');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load devices');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [categoryFilter]);

  useEffect(() => { load(true); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(false); };

  const confirmDelete = async () => {
    if (!deviceToDelete) return;
    try {
      setDeleting(true);
      const res = await apiService.deleteDevice(deviceToDelete);
      if (res.statusCode === 1000) {
        showNotification('success', 'Deleted', 'Device removed');
        setShowDeleteModal(false);
        setDeviceToDelete(null);
        load(false);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to delete');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderContent = () => {
    if (loading && devices.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Loading devices...</Text>
        </View>
      );
    }

    if (error && devices.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>Unable to Load Devices</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button title="Try Again" onPress={() => load(true)} variant="primary" style={styles.retryButton} />
        </View>
      );
    }

    if (devices.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="hardware-chip-outline" size={64} color={colors.border} />
          <Text style={styles.errorTitle}>No devices yet</Text>
          <Text style={styles.errorText}>Add scanners, printers and cameras your business uses</Text>
          <Button title="Add Device" onPress={() => navigateTo('device-create')} variant="primary" style={styles.retryButton} />
        </View>
      );
    }

    return (
      <View style={styles.listContainer}>
        <FlatList
          data={devices}
          keyExtractor={(item) => item._id}
          renderItem={({ item }) => (
            <DeviceCard
              device={item}
              onEdit={() => navigateTo('device-edit', item._id)}
              onDelete={() => { setDeviceToDelete(item._id); setShowDeleteModal(true); }}
            />
          )}
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
      </View>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <View style={styles.container}>
        {/* Header — matches ProductsListScreen pattern */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <View style={styles.titleRow}>
                <Text style={styles.headerTitle}>Devices</Text>
              </View>
              {!loading && (
                <Text style={styles.headerSubtitle}>
                  {total} {total === 1 ? 'device' : 'devices'}
                </Text>
              )}
            </View>

            <View style={styles.headerActions}>
              <TouchableOpacity
                style={styles.addButton}
                onPress={() => navigateTo('device-create')}
                activeOpacity={0.8}
              >
                <Ionicons name="add" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
          </View>

          {/* Category filter chips */}
          <View style={styles.filterRow}>
            {FILTERS.map((f) => (
              <TouchableOpacity
                key={f.label}
                style={[styles.filterChip, categoryFilter === f.key && styles.filterChipActive]}
                onPress={() => setCategoryFilter(f.key)}
                activeOpacity={0.7}
              >
                <Text style={[styles.filterChipText, categoryFilter === f.key && styles.filterChipTextActive]}>
                  {f.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {renderContent()}
      </View>

      <ConfirmationModal
        visible={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeviceToDelete(null); }}
        onConfirm={confirmDelete}
        title="Remove Device"
        message="Are you sure you want to remove this device? This cannot be undone."
        confirmText="Remove"
        cancelText="Cancel"
        type="error"
        loading={deleting}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  // ─── Header (mirrors ProductsListScreen) ──────────────────────────────────
  header: {
    backgroundColor: colors.background.surface,
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  headerLeft: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.xs,
  },
  headerTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  // ─── Filter chips ─────────────────────────────────────────────────────────
  filterRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    paddingBottom: spacing.sm,
  },
  filterChip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  filterChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  filterChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  filterChipTextActive: {
    color: colors.white,
  },
  // ─── List ─────────────────────────────────────────────────────────────────
  listContainer: {
    flex: 1,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  separator: { height: spacing.sm },
  // ─── Card ─────────────────────────────────────────────────────────────────
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    gap: spacing.md,
    ...designTokens.shadows.sm,
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: designTokens.borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardBody: { flex: 1 },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: 2 },
  cardName: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  statusDot: { width: 7, height: 7, borderRadius: 4 },
  statusLabel: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium },
  cardCategory: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginBottom: 2,
  },
  cardDetail: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  cardActions: { flexDirection: 'row', gap: spacing.xs },
  actionBtn: { padding: spacing.xs },
  // ─── States ───────────────────────────────────────────────────────────────
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    fontFamily: typography.fontFamily.regular,
  },
  retryButton: {
    minWidth: 160,
  },
});
